import { useState } from "react";
import { BadgeCheck, CreditCard, FileCheck, Shield } from "lucide-react";
import type { CatalogProduct } from "@/lib/catalog";
import { variantLabel } from "@/lib/catalog";
import { BuyLink } from "@/components/BuyLink";
import { RatingStars } from "@/components/RatingStars";
import { YberaPrice } from "@/components/YberaPrice";
import { cn } from "@/lib/utils";
import { SmartImage } from "@/components/SmartImage";

type OfferHeroProps = {
  eyebrow?: string;
  h1: string;
  h1Highlight?: string;
  subtitle: string;
  imageAlt: string;
  products: CatalogProduct[];
};

const TRUST = [
  { icon: Shield, label: "Parceiro oficial Ybera" },
  { icon: FileCheck, label: "Nota fiscal da marca" },
  { icon: BadgeCheck, label: "Sem formol" },
  { icon: CreditCard, label: "Pix e 6x sem juros" },
];

export function OfferHero({ eyebrow, h1, h1Highlight, subtitle, imageAlt, products }: OfferHeroProps) {
  const variants = products.filter((product) => product.officialUrl);
  const fallback = products[0];
  const [selectedSlug, setSelectedSlug] = useState(variants[0]?.slug ?? fallback?.slug);
  const selected =
    variants.find((product) => product.slug === selectedSlug) ?? variants[0] ?? fallback;

  if (!selected) return null;

  const gallery = selected.images.length ? selected.images : selected.image ? [selected.image] : [];

  return (
    <section className="border-b border-border bg-background">
      <div className="container grid items-start gap-10 py-10 md:grid-cols-2 md:py-14">
        <div>
          <div className="overflow-hidden rounded-3xl border border-border bg-muted">
            {gallery[0] ? (
              <SmartImage
                src={gallery[0]}
                alt={imageAlt}
                width={720}
                height={720}
                className="aspect-square w-full object-cover"
                priority
                sizes="(max-width: 768px) 92vw, 36rem"
              />
            ) : null}
          </div>
          {gallery.length > 1 ? (
            <div className="mt-3 flex gap-2">
              {gallery.slice(0, 4).map((src) => (
                <SmartImage
                  key={src}
                  src={src}
                  alt=""
                  width={88}
                  height={88}
                  cdnWidth={160}
                  className="h-16 w-16 rounded-lg border border-border object-cover"
                  sizes="64px"
                />
              ))}
            </div>
          ) : null}
        </div>

        <div>
          <RatingStars className="mb-3" />
          {eyebrow ? (
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary-text">{eyebrow}</p>
          ) : null}
          <p className="mb-3 inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary-text">
            Mais vendido · Fashion Gold
          </p>
          <h1 className="font-sans text-4xl font-extrabold leading-tight tracking-tight md:text-5xl">
            {h1}
            {h1Highlight ? (
              <span className="mt-2 block text-xl text-primary-text md:text-2xl">{h1Highlight}</span>
            ) : null}
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">{subtitle}</p>

          {variants.length > 1 ? (
            <div className="mt-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutral-900">Tamanho</p>
              <div className="flex flex-wrap gap-2">
                {variants.map((product) => (
                  <button
                    key={product.slug}
                    type="button"
                    onClick={() => setSelectedSlug(product.slug)}
                    className={cn(
                      "rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
                      product.slug === selected.slug
                        ? "border-neutral-900 bg-neutral-900 text-white"
                        : "border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100",
                    )}
                  >
                    {variantLabel(product)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <YberaPrice product={selected} size="pdp" className="mt-6" />

          <div className="mt-6 flex flex-col gap-3">
            <BuyLink product={selected} className="h-14 w-full text-base sm:w-auto">
              Comprar na Ybera
            </BuyLink>
            <p className="text-xs text-muted-foreground">
              Você vai para <strong>ybera.com</strong> como parceiro oficial, com desconto especial. Pagamento, estoque e
              nota fiscal são da Ybera Group.
            </p>
          </div>

          <ul className="mt-8 grid grid-cols-2 gap-3 text-sm">
            {TRUST.map((item) => (
              <li key={item.label} className="flex items-center gap-2 text-foreground">
                <item.icon className="h-4 w-4 shrink-0 text-primary-text" aria-hidden />
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
